import { Component, Input } from '@angular/core';

interface PasswordRule {
  label: string;
  test: (password: string) => boolean;
}

/** Mirrors ASP.NET Identity's default PasswordOptions, which the server enforces (Startup.cs keeps the defaults).
 * Identity counts any character that is not a letter or digit as "non alphanumeric". */
const PASSWORD_RULES: PasswordRule[] = [
  { label: 'At least 6 characters', test: (p) => p.length >= 6 },
  { label: 'An uppercase letter', test: (p) => /\p{Lu}/u.test(p) },
  { label: 'A lowercase letter', test: (p) => /\p{Ll}/u.test(p) },
  { label: 'A digit (0-9)', test: (p) => /[0-9]/.test(p) },
  { label: 'A special character, such as ! or #', test: (p) => /[^\p{L}\p{Nd}]/u.test(p) },
];

export function passwordMeetsRules(password: string | null | undefined): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(password ?? ''));
}

/** Live checklist under a new-password field, so the user sees each rule pass before they submit. */
@Component({
  selector: 'prox-password-rules',
  template: `
    <ul class="prox-password-rules">
      @for (rule of rules; track rule.label) {
        <li [class.met]="rule.test(password ?? '')">
          <i class="pi" [class.pi-check-circle]="rule.test(password ?? '')" [class.pi-circle]="!rule.test(password ?? '')"></i>
          {{ rule.label }}
        </li>
      }
      @if (confirm !== undefined) {
        <li [class.met]="!!password && password === confirm">
          <i class="pi" [class.pi-check-circle]="!!password && password === confirm" [class.pi-circle]="!password || password !== confirm"></i>
          Both passwords match
        </li>
      }
    </ul>
  `,
  styles: [
    `
      .prox-password-rules {
        list-style: none;
        margin: 0;
        padding: 0;
        font-size: 0.875rem;
        color: var(--p-text-muted-color, #6b7280);
      }
      .prox-password-rules li {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.125rem 0;
      }
      .prox-password-rules li.met {
        color: var(--p-green-600, #16a34a);
      }
    `,
  ],
})
export class PasswordRulesComponent {
  @Input() password: string | null | undefined = '';
  /** Pass the confirm field to add a "Both passwords match" line. */
  @Input() confirm: string | null | undefined = undefined;
  rules = PASSWORD_RULES;
}
